import { useCallback, useEffect, useRef, useState } from "react";

import {
  createChat,
  getChatMessages,
  listAgents,
  listChats,
  sendMessageStream,
} from "./api";
import {
  AgentId,
  AgentInfo,
  AIProviderName,
  ChatMessage,
  ChatSummary,
} from "./types";
import { nemoErrorReply } from "./nemoError";

export const useNemoChat = () => {
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<AgentId | null>(null);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const activeChatIdRef = useRef<string | null>(null);
  activeChatIdRef.current = activeChatId;
  const selectedAgentIdRef = useRef<AgentId | null>(null);
  selectedAgentIdRef.current = selectedAgentId;

  /** Stream de IA em andamento (se houver), para poder abortar ao trocar de conversa/desmontar. */
  const streamAbortControllerRef = useRef<AbortController | null>(null);

  const abortActiveStream = useCallback(() => {
    streamAbortControllerRef.current?.abort();
    streamAbortControllerRef.current = null;
  }, []);

  useEffect(() => abortActiveStream, [abortActiveStream]);

  const activeChat = chats.find((chat) => chat.id === activeChatId) || null;

  const refreshAgents = useCallback(async () => {
    const result = await listAgents();
    setAgents(result);
  }, []);

  const refreshChats = useCallback(async (): Promise<ChatSummary[]> => {
    const result = await listChats();
    setChats(result);
    return result;
  }, []);

  const openChat = useCallback(
    async (chatId: string) => {
      if (activeChatIdRef.current !== chatId) {
        abortActiveStream();
      }

      activeChatIdRef.current = chatId;
      setActiveChatId(chatId);
      setLoadingChat(true);
      setMessages([]);

      try {
        const result = await getChatMessages(chatId);
        if (activeChatIdRef.current === chatId) {
          setMessages(result);
        }
      } finally {
        if (activeChatIdRef.current === chatId) {
          setLoadingChat(false);
        }
      }
    },
    [abortActiveStream],
  );

  /** Limpa a UI sem criar registro no banco. */
  const startNewChat = useCallback(() => {
    abortActiveStream();
    activeChatIdRef.current = null;
    setActiveChatId(null);
    setMessages([]);
    setLoadingChat(false);
    setSelectedAgentId(null);
  }, [abortActiveStream]);

  const ensureActiveChat = useCallback(
    async (provider?: AIProviderName): Promise<string> => {
      if (activeChatIdRef.current) return activeChatIdRef.current;

      const chat = await createChat(provider, selectedAgentIdRef.current);
      activeChatIdRef.current = chat.id;
      setChats((prev) => [chat, ...prev]);
      setActiveChatId(chat.id);
      return chat.id;
    },
    [],
  );

  const sendMessage = useCallback(
    async (
      text: string,
      files: File[],
      provider?: AIProviderName,
    ): Promise<boolean> => {
      let chatId: string;
      try {
        chatId = await ensureActiveChat(provider);
      } catch (error) {
        return false;
      }

      const assistantMessageId = `${Date.now()}-assistant`;

      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-user`,
          role: "user",
          content: text,
          attachments: files.map((file) => ({
            originalName: file.name,
            storageKey: "",
            mimeType: file.type,
            size: file.size,
          })),
        },
        {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          attachments: [],
          pending: true,
        },
      ]);
      setSending(true);

      const abortController = new AbortController();
      streamAbortControllerRef.current = abortController;

      try {
        const result = await sendMessageStream({
          chatId,
          message: text,
          files,
          signal: abortController.signal,
          onDelta: (delta) => {
            if (activeChatIdRef.current !== chatId) return;
            setMessages((prev) =>
              prev.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, content: message.content + delta }
                  : message,
              ),
            );
          },
          onStatus: (label) => {
            if (activeChatIdRef.current !== chatId) return;
            setMessages((prev) =>
              prev.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, statusLabel: label }
                  : message,
              ),
            );
          },
        });

        if (result.type === "error") {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === assistantMessageId
                ? {
                    ...message,
                    content: nemoErrorReply(result.message, message.content),
                    pending: false,
                    statusLabel: undefined,
                  }
                : message,
            ),
          );
        } else if (activeChatIdRef.current === chatId) {
          const freshMessages = await getChatMessages(chatId);
          if (activeChatIdRef.current === chatId) {
            setMessages(freshMessages);
          }

          setChats((prev) =>
            [...prev]
              .map((chat) =>
                chat.id === chatId
                  ? {
                      ...chat,
                      title: result.title || chat.title,
                      provider: result.provider || chat.provider,
                      updatedAt: new Date().toISOString(),
                    }
                  : chat,
              )
              .sort(
                (a, b) =>
                  new Date(b.updatedAt).getTime() -
                  new Date(a.updatedAt).getTime(),
              ),
          );
        }
      } catch (error) {
        if ((error as { name?: string })?.name !== "AbortError") {
          const serverMessage =
            error instanceof Error ? error.message : undefined;
          setMessages((prev) =>
            prev.map((message) =>
              message.id === assistantMessageId
                ? {
                    ...message,
                    content: nemoErrorReply(serverMessage, message.content),
                    pending: false,
                    statusLabel: undefined,
                  }
                : message,
            ),
          );
        }
      } finally {
        if (streamAbortControllerRef.current === abortController) {
          streamAbortControllerRef.current = null;
        }
      }

      setSending(false);
      return true;
    },
    [ensureActiveChat],
  );

  return {
    agents,
    selectedAgentId,
    setSelectedAgentId,
    chats,
    activeChat,
    activeChatId,
    messages,
    sending,
    loadingChat,
    refreshAgents,
    refreshChats,
    openChat,
    startNewChat,
    sendMessage,
  };
};
